import moment from 'moment';
import { isEqual } from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import { HiOutlineEye } from 'react-icons/hi2';
import { MdOutlineModeEditOutline } from "react-icons/md";
import { useSelector } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import { ActionIcon, Button, Text, Tooltip, cn } from 'rizzui';
import { toast } from 'sonner';
import DeletePopover from '../../../components/buttons/DeletePopover';
import StatusDropdown from '../../../components/forms/StatusDropdown';
import { usePrivilege } from '../../../components/priviledge/PriviledgeProvider';
import { useFiltersPR } from '../../../components/providers/FiltersProvider';
import CustomTable from '../../../components/table/CustomTable';
import { FilterDrawerView } from '../../../components/table/components/TableFilter';
import useInvoiceStates from '../../../hooks/api-calls/invoices/useInvoiceStates';
import useKoReasons from '../../../hooks/api-calls/useKoReasons';
import useFetch from '../../../hooks/useFetch';
import useHandleDownloadDocument from '../../../hooks/useDownloadDocument';
import useHandleErrors from '../../../hooks/useHandleErrors';
import Page from '../../../layout/Page/Page';
import { RootState } from '../../../redux/store';
import { menuRoutes } from '../../../router/menu';
import { InvoiceService } from '../../../services/invoice/invoiceService';
import { Invoice, InvoicesApiResponse } from '../../../type/entities/invoice-type';
import ExternalInvoicingModal from '../components/ExternalInvoicingModal';
import InvoiceRectificationModal from '../profile/components/InvoiceRectificationModal';
import InvoicesFilters from './InvoicesFilters';
import { fixNumber } from '../../../utils/formatNumber';
import { decodeQueryString } from '../../../utils/transformUrl';
import { FaFileInvoiceDollar, FaPrint } from 'react-icons/fa6';

export const INVOICE_TYPES = [
    { label: 'Factura Cliente', value: 'client_invoice', permission: 'show_client_invoices' },
    { label: 'Factura Interna', value: 'internal_invoice', permission: 'show_internal_invoices' },
    { label: 'Factura Tutor', value: 'tutor_invoice', permission: 'show_tutor_invoices' },
    { label: 'Factura Impartidora', value: 'providing', permission: 'show_internal_invoices' },
    { label: 'Factura Plataforma', value: 'platform', permission: 'show_internal_invoices' },
    { label: 'Factura Servicios', value: 'services', permission: 'show_internal_invoices' },
    { label: 'Factura Atípica', value: 'non_standard_invoice', permission: 'show_non_standard_invoices' },
    { label: 'Factura Rectificativa', value: 'rectification_invoice', permission: 'show_rectification_invoices' },
];

interface InvoicesListProps { }

const columnOrder = [
    'id',
    'invoiceType',
    'number',
    'client',
    'totalPrice',
    'taxableAmount',
    'tax',
    'totalTaxesAmount',
    'totalProducts',
    'currentStatus',
    'corporation',
    'providingCorporation',
    'invoiceDate',
    'paymentDate',
    'expirationDate',
    'createdAt',
    'updatedAt',
    'isRectificationInvoice',
    'invoiceDates',
];

const InvoicesList: React.FC<InvoicesListProps> = ({ }) => {

    // HOOKS

    const { user } = useSelector((state: RootState) => state.auth);
    const { userCan } = usePrivilege();
    const { filters, updateFilters, updateFilterOrder, updatePage, updatePageSize, resetFilters, setPage } = useFiltersPR();
    const location = useLocation();
    const service = new InvoiceService();
    const { handleErrors } = useHandleErrors();
    const { koReasonsOptions } = useKoReasons();
    const { getInvoiceStatesList } = useInvoiceStates({ active: true });
    const { handleDownloadDocumentNewTab } = useHandleDownloadDocument();

    // STATES

    const visibleTabs = INVOICE_TYPES.filter(t => userCan(t.permission, 'invoices'));
    const [openFilters, setOpenFilters] = useState<boolean>(false);
    const [isExternalInvoicingModalOpen, setIsExternalInvoicingModalOpen] = useState<boolean>(false);
    const [invoiceToRectify, setInvoiceToRectify] = useState<Invoice | null>(null);
    const [rectificationInvoiceLoadingId, setRectificationInvoiceLoadingId] = useState<string | null>(null);

    // FUNCTIONS

    // -----------------------------------------------------------------------------------------------------------------
    /**
     * EN: Function to resolve the active tab from the filters.
     * ES: Función para resolver el tab activo desde los filtros.
     *
     * @param invoiceType
     * @param isRectificationInvoice
     * @returns
     */
    // -----------------------------------------------------------------------------------------------------------------
    const resolveActiveTabFromFilters = (
        invoiceType: unknown,
        isRectificationInvoice: unknown
    ): string | null => {
        if (!invoiceType) {
            return isRectificationInvoice === true ? 'rectification_invoice' : null;
        }

        if (Array.isArray(invoiceType)) {
            if (invoiceType.length === 1) {
                return invoiceType[0];
            }
            return null;
        }

        return typeof invoiceType === 'string' ? invoiceType : null;
    };
    // -----------------------------------------------------------------------------------------------------------------

    const [activeTab, setActiveTab] = useState<string | null>(() => resolveActiveTabFromFilters( filters.filter_filters?.invoice_type, filters.filter_filters?.is_rectification_invoice ));

    // -----------------------------------------------------------------------------------------------------------------
    /**
     * EN: Function to handle the tab change.
     * ES: Función para manejar el cambio de tab.
     *
     * @param tabValue
     */
    // -----------------------------------------------------------------------------------------------------------------
    const handleTabChange = (tabValue: string | null) => {
        setActiveTab(tabValue);

        if (!tabValue) {
            updateFilters({ invoice_type: null });
        } else if (tabValue === 'rectification_invoice') {
            updateFilters({ invoice_type: null, is_rectification_invoice: true });
        } else if (tabValue === 'internal_invoice') {
            updateFilters({ invoice_type: ['internal_invoice'], is_rectification_invoice: false });
        } else {
            updateFilters({ invoice_type: [tabValue], is_rectification_invoice: false });
        }

        setPage(1);
    };
    // -----------------------------------------------------------------------------------------------------------------    

    // -----------------------------------------------------------------------------------------------------------------
    /**
     * EN: Function to fetch the invoices list.
     * ES: Función para obtener la lista de facturas.
     *
     * @returns
     */
    // -----------------------------------------------------------------------------------------------------------------
    const [data, loading, error, refetch] = useFetch(useCallback(async () => {
        const response = await service.listInvoices(filters);
        return response.getResponseData() as InvoicesApiResponse;
    }, [filters]));
    // -----------------------------------------------------------------------------------------------------------------


    // -----------------------------------------------------------------------------------------------------------------
    /**
     * EN: Function to handle the state change.
     * ES: Función para manejar el cambio de estado.
     *
     * @param invoiceId
     * @param newStateId
     * @param comment
     * @param koReasonId
     */
    // -----------------------------------------------------------------------------------------------------------------
    const handleStateChange = async (invoiceId: string, newStateId: string, comment: string, koReasonId?: string) => {
        try {
            const response = await service.changeInvoiceState(invoiceId, newStateId, comment, koReasonId);
            const responseData = response.getResponseData();

            if (responseData && responseData.success) {
                toast.success("Estado de la factura actualizado correctamente");
                refetch();
            } else if (responseData) {
                toast.error(responseData.message);
                refetch();
            }
        } catch (error: any) {
            handleErrors(error);
        }
    };
    // -----------------------------------------------------------------------------------------------------------------

    
    // -----------------------------------------------------------------------------------------------------------------
    /**
     * EN: Function to delete an invoice.
     * ES: Función para eliminar una factura.
     *
     * @param id
     */
    // -----------------------------------------------------------------------------------------------------------------
    const handleDelete = async (id: string) => {
        try {
            const response = await (await service.deleteInvoice(id)).getResponseData();
            if (response.success) {
                refetch();
                toast.success('Se ha eliminado correctamente');
            } else {
                handleErrors(response);
            }
        } catch (error) {
            handleErrors(error);
        }
    };
    // -----------------------------------------------------------------------------------------------------------------


    // -----------------------------------------------------------------------------------------------------------------
    /**
     * EN: Function to delete multiple invoices.
     * ES: Función para eliminar múltiples facturas.
     *
     * @param ids
     */
    // -----------------------------------------------------------------------------------------------------------------
    const handleMultiDelete = async (ids: string[]) => {
        try {
            const response = await (await service.deleteMultiInvoices(ids)).getResponseData();
            if (response.success) {
                refetch();
                toast.success('Se han eliminado correctamente');
            } else {
                handleErrors(response);
            }
        } catch (error) {
            handleErrors(error);
        }
    };
    // -----------------------------------------------------------------------------------------------------------------


    // -----------------------------------------------------------------------------------------------------------------
    /**
     * EN: Function to open the rectification modal.
     * ES: Función para abrir el modal de rectificación.
     *
     * @param invoiceId
     */
    // -----------------------------------------------------------------------------------------------------------------
    const handleOpenRectificationModal = async (invoiceId: string) => {
        setRectificationInvoiceLoadingId(invoiceId);
        try {
            const response = await service.getInvoiceById(invoiceId);
            const responseData = response.getResponseData();

            if (responseData?.success === false) {
                handleErrors(responseData);
                return;
            }

            const invoice = responseData?.data ?? responseData;

            if (!invoice?.id) {
                toast.error('No se ha podido cargar la factura para rectificar');
                return;
            }

            if (invoice.isRectificationInvoice) {
                toast.error('Las facturas rectificativas no se pueden volver a rectificar');
                return;
            }

            setInvoiceToRectify(invoice);
        } catch (error) {
            handleErrors(error);
        } finally {
            setRectificationInvoiceLoadingId(null);
        }
    };
    // -----------------------------------------------------------------------------------------------------------------


    // -----------------------------------------------------------------------------------------------------------------
    /**
     * EN: Function to download multiple invoices.
     * ES: Función para descargar múltiples facturas.
     *
     * @param selectedRows
     */
    // -----------------------------------------------------------------------------------------------------------------
    const handleDownloadMultiInvoices = async (selectedRows: any) => {
        try {
            const response = await (await service.printMultiInvoices(selectedRows)).getResponseData();
            if (response) {
                // Crear un enlace temporal para descargar el archivo
                const url = window.URL.createObjectURL(new Blob([response], { type: 'application/zip' }));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `facturas.zip`);
                document.body.appendChild(link);
                link.click();
                link.parentNode?.removeChild(link);
                window.URL.revokeObjectURL(url);
            } else {
                toast.error('No se han podido descargar las facturas seleccionadas.');
            }
        } catch (error) {
            handleErrors(error);
        }
    };
    // -----------------------------------------------------------------------------------------------------------------


    // -----------------------------------------------------------------------------------------------------------------
    /**
     * EN: Function to order the data to match the column order.
     * ES: Función para ordenar los datos para que coincidan con el orden de las columnas.
     *
     * @returns
     */
    // -----------------------------------------------------------------------------------------------------------------
    const orderedData = data?.data.map((row: any) => {
        const orderedRow: any = {};
        columnOrder.forEach((key) => {
            orderedRow[key] = row[key];
        });
        return orderedRow;
    });
    // -----------------------------------------------------------------------------------------------------------------


    // USE EFFECTS

    useEffect(() => {
        setActiveTab(resolveActiveTabFromFilters(
            filters.filter_filters?.invoice_type,
            filters.filter_filters?.is_rectification_invoice
        ));
    }, [filters.filter_filters?.invoice_type, filters.filter_filters?.is_rectification_invoice]);

    useEffect(() => {
        const decoded = decodeQueryString(location.search);
        const urlFilterFilters = decoded?.filter_filters;
        if (!urlFilterFilters) return;

        const currentFilters = filters.filter_filters ?? {};
        const menuFilterSnapshot = (filterFilters: Record<string, any>) => ({
            invoice_type: filterFilters.invoice_type ?? null,
            is_rectification_invoice: filterFilters.is_rectification_invoice ?? null,
        });

        if (isEqual(menuFilterSnapshot(currentFilters), menuFilterSnapshot(urlFilterFilters))) {
            return;
        }

        updateFilters({
            invoice_type: urlFilterFilters.invoice_type ?? null,
            is_rectification_invoice: urlFilterFilters.is_rectification_invoice ?? null,
        });

        if (decoded?.page && Number(decoded.page) !== Number(filters.page)) {
            setPage(Number(decoded.page));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search]);

    // RENDER

    return (
        <Page container='fluid'>
            {visibleTabs.length > 0 && (
                <div className="flex gap-1 mb-4 border-b border-muted overflow-x-auto">
                    <button
                        key="all"
                        onClick={() => handleTabChange(null)}
                        className={cn(
                            'px-4 py-2 text-sm font-medium whitespace-nowrap border-b-2 transition-colors',
                            activeTab === null
                                ? 'border-primary text-primary'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        )}
                    >
                        Todas
                    </button>
                    {visibleTabs.map(tab => (
                        <button
                            key={tab.value}
                            onClick={() => handleTabChange(tab.value)}
                            className={cn(
                                'px-4 py-2 text-sm font-medium whitespace-nowrap border-b-2 transition-colors',
                                activeTab === tab.value
                                    ? 'border-primary text-primary'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            )}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>
            )}
            {orderedData && (
                <CustomTable
                    largeTable
                    id={'invoices-table'}
                    data={orderedData}
                    columnOrder={columnOrder}
                    isLoading={loading}
                    columnsNotShown={['company', 'lines', 'invoiceDate', 'paymentDate', 'expirationDate', 'isRectificationInvoice', 'invoiceDates']}
                    additionalColumns={[
                        {
                            id: 'invoiceDates',
                            header: 'Fechas Factura',
                            render: (row: any) => {
                                const invoiceDate = row.row.original?.invoiceDate?.date;
                                const paymentDate = row.row.original?.paymentDate?.date;
                                const expirationDate = row.row.original?.expirationDate?.date;
                                return (
                                    <ul className='gap-x-4'>
                                        <li className='flex flex-nowrap pb-1'>
                                            <h6 className='text-nowrap text-gray-400'>Factura:&nbsp;</h6>
                                            <span className='text-nowrap'>{invoiceDate ? moment(invoiceDate).format('DD/MM/YY HH:mm') : 'N/A'}</span>
                                        </li>
                                        <li className='flex flex-nowrap pb-1'>
                                            <h6 className='text-nowrap text-gray-400'>Pago:&nbsp;</h6>
                                            <span className='text-nowrap'>{paymentDate ? moment(paymentDate).format('DD/MM/YY HH:mm') : 'N/A'}</span>
                                        </li>
                                        <li className='flex flex-nowrap pb-1'>
                                            <h6 className='text-nowrap text-gray-400'>Vencimiento:&nbsp;</h6>
                                            <span className='text-nowrap'>{expirationDate ? moment(expirationDate).format('DD/MM/YY HH:mm') : 'N/A'}</span>
                                        </li>
                                    </ul>
                                );
                            }
                        },
                    ]}
                    overrideColumns={[
                        {
                            key: 'invoiceType',
                            columnNameKey: 'invoiceType',
                            oneOfThesePermissions: [
                                { group: 'invoices', permission: 'show_client_invoices' },
                                { group: 'invoices', permission: 'show_internal_invoices' },
                                { group: 'invoices', permission: 'show_tutor_invoices' },
                                { group: 'invoices', permission: 'show_non_standard_invoices' },
                                { group: 'invoices', permission: 'show_rectification_invoices' },
                            ],
                            render: (row: any) => {
                                return INVOICE_TYPES.find(type => type.value === row.row.original.invoiceType)?.label || 'N/A';
                            }
                        },
                        {
                            key: 'number',
                            columnNameKey: 'invoiceNumber',
                            render: (row: any) => {
                                if (userCan('get_invoices', 'invoices')) {
                                    return (
                                        <Link to={`${menuRoutes.invoices.path}/${row.row.original.id}/profile${menuRoutes.invoices.profile.info}`}>
                                            <Text className='font-bold text-primary'>{row.row.original?.number}</Text>
                                        </Link>
                                    );
                                } else {
                                    return row.row.original?.number || 'N/A';
                                }
                            },
                        },
                        {
                            key: 'client',
                            render: (row: any) => {
                                if (userCan('get_clients', 'clients')) {
                                    return (
                                        <Link to={`${menuRoutes.clients.path}/${row.row.original?.client?.id}/profile${menuRoutes.clients.profile.info}`} className=''>
                                            <Text className='font-bold text-primary'>{row.row.original?.client?.companyName}</Text>
                                        </Link>
                                    );
                                } else {
                                    return row.row.original?.client?.companyName || 'N/A';
                                }
                            },
                        },
                        {
                            key: 'totalPrice',
                            render: (row: any) => {
                                return (
                                    <div className="text-center">
                                        {row.row.original?.totalPrice ? `${fixNumber(row.row.original?.totalPrice)}€` : 'N/A'}
                                    </div>
                                )
                            },
                        },
                        {
                            key: 'taxableAmount',
                            render: (row: any) => {
                                return (
                                    <div className="text-center">
                                        {row.row.original?.taxableAmount ? `${fixNumber(row.row.original?.taxableAmount)}€` : 'N/A'}
                                    </div>
                                )
                            },
                        },
                        {
                            key: 'currentStatus',
                            render: (row: any) => {
                                const canChangeStatus = userCan('edit_invoices', 'invoices');
                                return (
                                    <Tooltip
                                        size="sm"
                                        content={row.row.original?.currentStatus?.description}
                                        placement="top"
                                        color="invert"
                                    >
                                        <>
                                            <StatusDropdown
                                                entityId={row.row.original?.id}
                                                currentState={row.row.original?.currentStatus}
                                                statesOptions={getInvoiceStatesList(row.row.original?.currentStatus?.id)}
                                                handleStateChange={handleStateChange}
                                                title="Esta acción modificará el estado de la factura"
                                                disabled={!canChangeStatus}
                                                koReasonsOptions={koReasonsOptions}
                                                koReasonStatusEntity="invoice"
                                            />
                                        </>
                                    </Tooltip>
                                );
                            }
                        },
                        {
                            key: 'corporation',
                            render: (row: any) => {
                                return row.row.original?.corporation?.name || 'N/A';
                            },
                        },
                        {
                            key: 'providingCorporation',
                            render: (row: any) => {
                                return row.row.original?.providingCorporation?.name || 'N/A';
                            },
                        },
                        {
                            key: 'tax',
                            render: (row: any) => {
                                return (
                                    row.row.original?.tax ? `${row.row.original?.tax}%` : 'N/A'
                                );
                            },
                        },
                        {
                            key: 'totalTaxesAmount',
                            render: (row: any) => {
                                return (
                                    <div className="text-center">
                                        {row.row.original?.totalTaxesAmount ? `${fixNumber(row.row.original?.totalTaxesAmount)}€` : 'N/A'}
                                    </div>
                                )

                            },
                        },
                        {
                            key: 'totalProducts',
                            render: (row: any) => {
                                return (
                                    <div className="text-center">
                                        {row.row.original?.totalProducts ? `${row.row.original?.totalProducts}` : 'N/A'}
                                    </div>
                                )
                            },
                        },
                    ]}
                    actions={[
                        {
                            label: 'View',
                            permissions: { group: 'invoices', permission: 'get_invoices' },
                            render: (row: any) => {
                                return (
                                    <Tooltip
                                        size="sm"
                                        content={'Ver detalles'}
                                        placement="top"
                                        color="invert"
                                    >
                                        <Link to={`${menuRoutes.invoices.path}/${row.id}/profile${menuRoutes.invoices.profile.activityHistory}`}>
                                            <ActionIcon
                                                as="span"
                                                size="sm"
                                                variant="outline"
                                                className="hover:!border-gray-900 hover:text-gray-700"
                                            >
                                                <HiOutlineEye className="h-4 w-4" size={20} />
                                            </ActionIcon>
                                        </Link>
                                    </Tooltip>
                                );
                            },
                        },
                        {
                            label: 'Print',
                            permissions: { group: 'invoices', permission: 'print_invoices' },
                            render: (row: any) => {
                                const invoiceDate = row.invoiceDate?.date;
                                const fileName = invoiceDate
                                    ? `${moment(invoiceDate).format('DD-MM-YYYY')}_factura.pdf`
                                    : 'factura.pdf';

                                return (
                                    <Tooltip
                                        size="sm"
                                        content={'Descargar factura'}
                                        placement="top"
                                        color="invert"
                                    >
                                        <ActionIcon
                                            as="span"
                                            size="sm"
                                            variant="outline"
                                            className="hover:!border-gray-900 hover:text-gray-700 cursor-pointer"
                                            onClick={() => handleDownloadDocumentNewTab(service.printInvoice, row.id, fileName)}
                                        >
                                            <FaPrint className="h-4 w-4" size={20} />
                                        </ActionIcon>
                                    </Tooltip>
                                );
                            },
                        },
                        {
                            label: 'Rectify',
                            permissions: { group: 'invoices', permission: 'edit_invoices' },
                            render: (row: any) => {
                                if (row.isRectificationInvoice) return null;

                                return (
                                    <Tooltip
                                        size="sm"
                                        content={'Rectificar factura'}
                                        placement="top"
                                        color="invert"
                                    >
                                        <ActionIcon
                                            size="sm"
                                            variant="outline"
                                            className="hover:!border-gray-900 hover:text-gray-700"
                                            onClick={() => handleOpenRectificationModal(row.id)}
                                            disabled={rectificationInvoiceLoadingId === row.id}
                                        >
                                            <FaFileInvoiceDollar className="h-4 w-4" size={20} />
                                        </ActionIcon>
                                    </Tooltip>
                                );
                            },
                        },
                        {
                            label: 'Edit',
                            permissions: { group: 'invoices', permission: 'edit_invoices' },
                            render: (row: any) => {
                                return (
                                    <Tooltip
                                        size="sm"
                                        content={'Editar factura'}
                                        placement="top"
                                        color="invert"
                                    >
                                        <Link to={`${menuRoutes.invoices.path}/${row.id}/edit`}>
                                            <ActionIcon
                                                as="span"
                                                size="sm"
                                                variant="outline"
                                                className="hover:!border-gray-900 hover:text-gray-700"
                                            >
                                                <MdOutlineModeEditOutline className="h-4 w-4" size={20} />
                                            </ActionIcon>
                                        </Link>
                                    </Tooltip>
                                );
                            },
                        },
                        {
                            label: 'Delete',
                            permissions: { group: 'invoices', permission: 'delete_invoices' },
                            render: (row: any) => {
                                if (row.id === user?.id) return null;
                                return (
                                    <Tooltip
                                        size="sm"
                                        content={'Eliminar'}
                                        placement="top"
                                        color="invert"
                                    >
                                        <div>
                                            <DeletePopover
                                                title={`Eliminar factura`}
                                                description={`¿Estás seguro de que deseas eliminar a ${row.number}?`}
                                                onDelete={() => handleDelete(row.id)}
                                            />
                                        </div>
                                    </Tooltip>
                                );
                            },
                        },
                    ]}
                    multiActions={[
                        //{
                        //    label: 'Download Invoices',
                        //    render: (selectedRows: any) => {
                        //        return (
                        //            <Button
                        //                onClick={() => { handleDownloadMultiInvoices(selectedRows) }}
                        //                variant="outline"
                        //                color="secondary"
                        //                size="sm"
                        //            >
                        //                Descargar Facturas
                        //            </Button>
                        //        );
                        //    }
                        //},
                    ]}
                    handleMultipleDelete={handleMultiDelete}
                    filters={filters}
                    updateFilters={updateFilters}
                    updateFilterOrder={updateFilterOrder}
                    defaultOrder={filters.filter_order || undefined}
                    paginationData={{
                        pageSize: filters.limit,
                        currentPage: filters.page,
                        pageCount: (data as InvoicesApiResponse) ? data.lastPage : 1,
                        totalCount: data?.totalRegisters,
                        handlePagination: updatePage,
                        handlePerPage: updatePageSize,
                    }}
                    toggleFilters={() => setOpenFilters(!openFilters)}
                >
                    {(userCan('tutors_invoices', 'tutors') && activeTab === 'tutor_invoice') ? (
                        <Button
                            onClick={() => setIsExternalInvoicingModalOpen(true)}
                        >
                            Facturar Externas
                        </Button>
                    ): null}
                </CustomTable>
            )}

            <FilterDrawerView isOpen={openFilters} setOpenDrawer={setOpenFilters} drawerTitle={'Filtros Facturas'}>
                <InvoicesFilters filters={filters} updateFilters={updateFilters} resetFilters={resetFilters} />
            </FilterDrawerView>

            {isExternalInvoicingModalOpen && (
                <ExternalInvoicingModal
                    isOpen={isExternalInvoicingModalOpen}
                    onClose={() => setIsExternalInvoicingModalOpen(false)}
                    onSuccess={() => refetch()}
                />
            )}

            <InvoiceRectificationModal
                isOpen={Boolean(invoiceToRectify)}
                invoice={invoiceToRectify}
                onClose={() => setInvoiceToRectify(null)}
                onCompleted={() => refetch()}
            />
        </Page>
    );
};

export default InvoicesList;
